import { BreakpointProp } from "@/ui/lib";
import { DefinedDefaults } from "@/ui/lib/types";
import { ImgHTMLAttributes } from "react";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const IMAGE_SRCSET_MEDIA_MAP = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 2201,
} as const;

export const IMAGE_FIT_CLASS_MAP = {
  default: "w-full",
  cover: "w-full h-full absolute top-0 left-0 object-cover",
  contain: "w-full h-full absolute top-0 left-0 object-contain",
} as const;

export const IMAGE_RESPONSIVE_FIT_CLASS_MAP = {
  default: IMAGE_FIT_CLASS_MAP,
  xl: {
    "default": "xl:w-full",
    cover: "xl:w-full xl:h-full xl:absolute xl:top-0 xl:left-0 xl:object-cover",
    contain: "xl:w-full xl:h-full xl:absolute xl:top-0 xl:left-0 xl:object-contain",
  },
} as const;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TImageFit = keyof typeof IMAGE_FIT_CLASS_MAP;

export type TImageFitProp =
  BreakpointProp<typeof IMAGE_RESPONSIVE_FIT_CLASS_MAP>;

export type TSrcSetMedia = keyof typeof IMAGE_SRCSET_MEDIA_MAP;

export type TSrcSetImage = Pick<ImgHTMLAttributes<HTMLImageElement>, "width" | "height"> & {
  src?: string;
};

export type TSrcSet = Partial<Record<TSrcSetMedia, TSrcSetImage>>;

export type TImageSrcSetProps = {
  images?: TSrcSet
};

export type TImageDimensions = Pick<ImgHTMLAttributes<HTMLImageElement>, "width" | "height">;

export type TImageProps = {
  fit?: TImageFitProp;
  srcSet?: TSrcSet;
} & ImgHTMLAttributes<HTMLImageElement>;

type TImageDefaultProps = DefinedDefaults<
  TImageProps,
  "fit" |
  "loading"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const IMAGE_DEFAULTS = {
  fit: "default",
  loading: "lazy",
} satisfies TImageDefaultProps;
